import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { AdminPermission, AdminUserSessionContext } from '../../../viewmodels/AdminViewModels';
import { NavBar } from '../../../web/components/NavBar';
import { INavBarLink } from '../../../web/components/NavBarLink';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { Masthead } from '../../components/Masthead';
import { NavHeaderBackground } from '../../components/NavHeaderBackground';
import { isResellerSession } from '../../contexts/ResellerContext';
import { getSessionAdminAccountsUrl } from '../account/Accounts';
import { CompactWidth, HeaderHeight, styleSheet } from './styles';

const getNavBarLinks = (userSession: AdminUserSessionContext, isReseller: boolean): INavBarLink[] => {
	// Resellers only see Accounts
	if (isReseller) {
		return [
			{
				linkName: 'Accounts',
				path: getSessionAdminAccountsUrl(),
			},
		];
	}

	const hasManageTemplatesPermission = userSession.hasPermission(AdminPermission.ManageTemplates);
	const hasManageSurveyTemplatesPermission = userSession.hasPermission(AdminPermission.ManageSurveyTemplates);
	const hasManageAIAgentTemplatesPermission = userSession.hasPermission(AdminPermission.ManageAIAgentTemplates);
	const hasOnlyForDevelopersPermission = userSession.hasPermission(AdminPermission.OnlyForDevelopers);
	const hasSupportAccess = userSession.canAccessSupport || hasOnlyForDevelopersPermission;
	const hasReadAccountPermission =
		userSession.hasPermission(AdminPermission.ReadAccount) || hasOnlyForDevelopersPermission;
	const hasManageAdsIntegrationsPermission = userSession.hasPermission(AdminPermission.ManageAdsIntegrations);
	const hasCanViewOpenInvoicesPermission =
		userSession.hasPermission(AdminPermission.ViewOpenInvoices) ||
		userSession.hasPermission(AdminPermission.OnlyForDevelopers);

	return [
		...(hasReadAccountPermission
			? [
					{
						linkName: 'Accounts',
						path: getSessionAdminAccountsUrl(),
					},
				]
			: []),
		...(hasReadAccountPermission
			? [
					{
						linkName: 'Reports',
						path: '/reports',
					},
				]
			: []),
		...(hasCanViewOpenInvoicesPermission
			? [
					{
						linkName: 'Open Invoices',
						path: '/billing/openInvoices',
					},
				]
			: []),
		...(hasManageTemplatesPermission
			? [
					{
						linkName: 'Campaigns',
						path: '/campaigns',
					},
					{
						linkName: 'Automations',
						path: '/automations',
					},
				]
			: []),
		...(hasManageSurveyTemplatesPermission
			? [
					{
						linkName: 'Surveys',
						path: '/surveys',
					},
				]
			: []),
		...(hasManageAIAgentTemplatesPermission
			? [
					{
						linkName: 'AI Agents',
						icon: <SparklesIcon height={18} width={18} />,
						path: '/ai-agent-templates',
					},
				]
			: []),
		...(hasSupportAccess
			? [
					{
						linkName: 'Support',
						path: '/support',
					},
				]
			: []),
		...(hasReadAccountPermission
			? [
					{
						linkName: 'Tools',
						path: '/tools',
					},
				]
			: []),
		...(hasManageAdsIntegrationsPermission
			? [
					{
						linkName: 'Ads',
						path: '/ads/meta',
					},
				]
			: []),
		...(hasOnlyForDevelopersPermission
			? [
					{
						linkName: 'DevTools',
						path: '/devtools',
					},
				]
			: []),
	];
};

function _AdminDashboardPageLayout({
	children,
	compactNav = false,
	navHeader,
	navStyles = [],
	sidebarChildren,
	contentStyles = [],
	mastheadHeight = HeaderHeight,
	fullBleed = false,
	mastheadNavigationFillsSpace = false,
	showMastheadDivider = true,
	narrowLayout = false,
}: {
	children: React.ReactNode;
	compactNav?: boolean;
	navHeader?: React.ReactNode;
	navStyles?: StyleDeclarationValue[];
	sidebarChildren?: React.ReactNode;
	contentStyles?: StyleDeclarationValue[];
	/** Override the standard admin masthead height while preserving the page's flex layout. */
	mastheadHeight?: number;
	/** Remove page gutters and use the compact app-shell gutter in the masthead. */
	fullBleed?: boolean;
	/** Allow portal content in the masthead to consume the available horizontal space. */
	mastheadNavigationFillsSpace?: boolean;
	showMastheadDivider?: boolean;
	/** Support is the only admin surface that may collapse below the shared 768px shell minimum. */
	narrowLayout?: boolean;
}) {
	const userSession = useAdminUserSession();
	const isReseller = isResellerSession(userSession);
	const isWideScreen = useMediaQuery({ minWidth: CompactWidth });
	const shouldCollapseNav = !isWideScreen || compactNav;
	return (
		<div
			className={`admin-container ${css(styleSheet.container, narrowLayout && styleSheet.narrowLayoutContainer)} ${
				shouldCollapseNav ? 'admin-container-compact-nav' : ''
			}`}
		>
			<NavBar
				className={`admin-container-nav ${css(
					shouldCollapseNav ? styleSheet.compactNav : styleSheet.nav,
					narrowLayout && styleSheet.narrowLayoutNav
				)}`}
				compactLayout={shouldCollapseNav}
				header={
					navHeader ?? (
						<NavHeaderBackground compactLayout={shouldCollapseNav} styles={[styleSheet.navHeaderBackground]} />
					)
				}
				links={getNavBarLinks(userSession, isReseller)}
				styles={navStyles}
			/>
			<div className={css(styleSheet.rightContainer, narrowLayout && styleSheet.narrowLayoutRightContainer)}>
				<div className={css(styleSheet.containerContent, ...contentStyles)}>
					<Masthead
						className={`admin-container-header ${css(styleSheet.masthead, fullBleed && styleSheet.fullBleedMasthead)}`}
						height={mastheadHeight}
						navigationFillsSpace={mastheadNavigationFillsSpace}
						showDivider={showMastheadDivider}
					/>
					<div className={`admin-container-right-container-content ${css(styleSheet.rightContainerScroller)}`}>
						<div
							className={css(
								styleSheet.rightContainerScrollerContent,
								fullBleed && styleSheet.fullBleedScrollerContent
							)}
						>
							{children}
						</div>
					</div>
				</div>
			</div>
			{sidebarChildren}
		</div>
	);
}

export const AdminDashboardPageLayout = observer(_AdminDashboardPageLayout);
